import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { getAuthToken } from '../../../utils/authTokens';
import {
    CoffeeBean,
    CoffeeBrand,
    CoffeeCapsule,
    CoffeeMachine,
    CoffeeMachineType,
    CoffeeRoastLevel,
    SupermarketFormat,
    SupermarketType,
} from '../CoffeeCardWeb/types';
import {
    coffeeBeanPaths,
    coffeeBrandPaths,
    coffeeCapsulePaths,
    coffeeMachinePaths,
    coffeeMasterPaths,
    coffeeRatingPaths,
} from './paths';

// Backend envelopes (see /api/coffee/v2/brands* + /ratings/supermarket).
interface BrandListResponse {
    success: boolean;
    data: CoffeeBrand[];
    count: number;
}

interface BrandCreateResponse {
    success: boolean;
    data: CoffeeBrand;
    created: boolean;
}

interface SupermarketRatingResponse {
    coffeeRateId: string;
    overallRating: number;
    rateColor: string;
    brand?: CoffeeBrand;
}

export interface SupermarketRatingPayload {
    brandRef?: string;
    brandName?: string;
    // the brand's product type: typeKey for an existing one, else typeName (+ typeFormat) to create-or-get a pending one
    typeKey?: string;
    typeName?: string;
    typeFormat?: SupermarketFormat;
    rating?: Record<string, number>;
    quickRate?: number;
    description?: string;
    city?: string;
    country?: string;
}

// Machine envelopes (see /api/coffee/v2/machines*). List returns data[],
// single returns data with count:1; all share the house { success, data, count } shape.
interface MachineListResponse {
    success: boolean;
    data: CoffeeMachine[];
    count: number;
}

interface MachineResponse {
    success: boolean;
    data: CoffeeMachine;
    count: number;
}

export interface RegisterMachinePayload {
    type: CoffeeMachineType;
    brand: string;
    model: string;
    specs?: Record<string, unknown>;
    defaultBrew?: Record<string, unknown>;
}

// PATCH /machines/:id accepts any subset of these (at least one required server-side).
export interface UpdateMachinePayload {
    brand?: string;
    model?: string;
    specs?: Record<string, unknown>;
    defaultBrew?: Record<string, unknown>;
}

// Optional `home` block on a normal/home rating create (POST /ratings). Only verified
// Coffee Masters can populate it; the backend silently ignores it otherwise. `brew` is a
// flexible config (manual: { method:'manual', doseGrams, grind, notes }; capsule:
// { method:'capsule', capsuleBrand, capsuleType, intensity }). beanRef/capsuleRef link a
// registered bean/capsule so its aggregate score picks up this beverage's rating.
export interface HomeRatingPayload {
    machineRef?: string;
    beanRef?: string;
    capsuleRef?: string;
    brew?: Record<string, unknown>;
}

// Bean / capsule envelopes (see /api/coffee/v2/beans* + /capsules*). Same house shape as machines.
interface BeanListResponse { success: boolean; data: CoffeeBean[]; count: number; }
interface BeanResponse { success: boolean; data: CoffeeBean; count: number; }
interface CapsuleListResponse { success: boolean; data: CoffeeCapsule[]; count: number; }
interface CapsuleResponse { success: boolean; data: CoffeeCapsule; count: number; }

export interface RegisterBeanPayload {
    name: string;
    roaster?: string;
    origin?: string;
    roastLevel?: CoffeeRoastLevel;
    process?: string;
    notes?: string;
}
export type UpdateBeanPayload = Partial<RegisterBeanPayload>;

export interface RegisterCapsulePayload {
    brand: string;
    name: string;
    intensity?: number;
    notes?: string;
}
export type UpdateCapsulePayload = Partial<RegisterCapsulePayload>;

// Coffee Master status + request flow (see /api/coffee/v2/coffee-master/*).
export type CoffeeMasterRequestStatus = 'pending' | 'approved' | 'rejected';
export interface CoffeeMasterStatus {
    isCoffeeMaster: boolean;
    request: { status: CoffeeMasterRequestStatus; createdAt: string; reviewedAt: string | null } | null;
}
interface CoffeeMasterStatusResponse { success: boolean; data: CoffeeMasterStatus; }

// Admin review-queue row (GET /coffee-master/requests). userId is populated.
export interface CoffeeMasterRequest {
    _id: string;
    userId: { _id: string; username: string; userEmoji?: string; email?: string } | string;
    username: string;
    status: CoffeeMasterRequestStatus;
    message?: string;
    createdAt: string;
    reviewedAt?: string | null;
}
interface CoffeeMasterRequestsResponse {
    success: boolean;
    data: CoffeeMasterRequest[];
    stats: { pending: number };
    count: number;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

// Autocomplete brand lookup (server caps at 10).
export async function searchBrands(q: string): Promise<CoffeeBrand[]> {
    const response = await apiClient.get<BrandListResponse>(
        `${url}${coffeeBrandPaths.search()}?q=${encodeURIComponent(q)}`,
    );
    return response.data.data;
}

// Full brand registry, optionally filtered by query.
export async function listBrands(q?: string): Promise<CoffeeBrand[]> {
    const query = q ? `?q=${encodeURIComponent(q)}` : '';
    const response = await apiClient.get<BrandListResponse>(`${url}${coffeeBrandPaths.list()}${query}`);
    return response.data.data;
}

// Create-or-get a brand by name (dedup on normalized name server-side). New brands are
// created PENDING (admin review) — they still work for the submitter's rating immediately.
export async function createBrand(name: string): Promise<{ brand: CoffeeBrand; created: boolean }> {
    const response = await apiClient.post<BrandCreateResponse>(
        `${url}${coffeeBrandPaths.create()}`,
        { name },
        authHeaders(),
    );
    return { brand: response.data.data, created: response.data.created };
}

// The approved product types of a brand (with their format-derived rating criteria).
export async function getBrandTypes(idOrSlug: string): Promise<SupermarketType[]> {
    const response = await apiClient.get<{ success: boolean; data: SupermarketType[] }>(
        `${url}${coffeeBrandPaths.types(idOrSlug)}`,
        authHeaders(),
    );
    return response.data.data;
}

// Propose a new product type for a brand (create-or-get; created pending review).
export async function proposeBrandType(
    idOrSlug: string,
    name: string,
    format: SupermarketFormat,
): Promise<SupermarketType> {
    const response = await apiClient.post<{ success: boolean; data: SupermarketType; created: boolean }>(
        `${url}${coffeeBrandPaths.proposeType(idOrSlug)}`,
        { name, format },
        authHeaders(),
    );
    return response.data.data;
}

// ── Supermarket brand/type admin moderation (admin only) ─────────────────────
export interface PendingBrand extends CoffeeBrand {
    brandPending: boolean;
    createdBy?: { _id: string; username: string; userEmoji?: string } | null;
    createdAt?: string;
    pendingTypes: SupermarketType[];
}
export async function listPendingBrands(): Promise<PendingBrand[]> {
    const response = await apiClient.get<{ success: boolean; data: PendingBrand[] }>(
        `${url}${coffeeBrandPaths.pending()}`,
        authHeaders(),
    );
    return response.data.data;
}
export async function approveBrand(id: string): Promise<void> {
    await apiClient.post(`${url}${coffeeBrandPaths.approve(id)}`, {}, authHeaders());
}
export async function rejectBrand(id: string): Promise<void> {
    await apiClient.post(`${url}${coffeeBrandPaths.reject(id)}`, {}, authHeaders());
}
export async function approveBrandType(id: string, key: string): Promise<void> {
    await apiClient.post(`${url}${coffeeBrandPaths.approveType(id, key)}`, {}, authHeaders());
}
export async function rejectBrandType(id: string, key: string): Promise<void> {
    await apiClient.post(`${url}${coffeeBrandPaths.rejectType(id, key)}`, {}, authHeaders());
}

// Submit a supermarket rating. Pass brandRef for an existing brand, else brandName to create-or-get.
// The image step afterwards is the same as normal ratings (POST /ratings/:id/image).
export async function submitSupermarketRating(
    payload: SupermarketRatingPayload,
): Promise<{ coffeeRateId: string; overallRating: number; rateColor: string }> {
    const response = await apiClient.post<SupermarketRatingResponse>(
        `${url}${coffeeRatingPaths.createSupermarket()}`,
        payload,
        authHeaders(),
    );
    const { coffeeRateId, overallRating, rateColor } = response.data;
    return { coffeeRateId, overallRating, rateColor };
}

// List the caller's registered machines (active first, then most-recently-updated).
export async function listMyMachines(): Promise<CoffeeMachine[]> {
    const response = await apiClient.get<MachineListResponse>(
        `${url}${coffeeMachinePaths.list()}`,
        authHeaders(),
    );
    return response.data.data;
}

// Register a new machine for the caller (verified machine owners only).
export async function registerMachine(payload: RegisterMachinePayload): Promise<CoffeeMachine> {
    const response = await apiClient.post<MachineResponse>(
        `${url}${coffeeMachinePaths.create()}`,
        payload,
        authHeaders(),
    );
    return response.data.data;
}

// Update brand/model/specs/defaultBrew on one of the caller's machines.
export async function updateMachine(id: string, patch: UpdateMachinePayload): Promise<CoffeeMachine> {
    const response = await apiClient.patch<MachineResponse>(
        `${url}${coffeeMachinePaths.byId(id)}`,
        patch,
        authHeaders(),
    );
    return response.data.data;
}

// Soft-retire one of the caller's machines (isActive:false).
export async function deactivateMachine(id: string): Promise<CoffeeMachine> {
    const response = await apiClient.patch<MachineResponse>(
        `${url}${coffeeMachinePaths.deactivate(id)}`,
        {},
        authHeaders(),
    );
    return response.data.data;
}

// ── Beans (Coffee Master setup) ──────────────────────────────────────────────
export async function listMyBeans(): Promise<CoffeeBean[]> {
    const response = await apiClient.get<BeanListResponse>(`${url}${coffeeBeanPaths.list()}`, authHeaders());
    return response.data.data;
}
export async function registerBean(payload: RegisterBeanPayload): Promise<CoffeeBean> {
    const response = await apiClient.post<BeanResponse>(`${url}${coffeeBeanPaths.create()}`, payload, authHeaders());
    return response.data.data;
}
export async function updateBean(id: string, patch: UpdateBeanPayload): Promise<CoffeeBean> {
    const response = await apiClient.patch<BeanResponse>(`${url}${coffeeBeanPaths.byId(id)}`, patch, authHeaders());
    return response.data.data;
}
export async function deactivateBean(id: string): Promise<CoffeeBean> {
    const response = await apiClient.patch<BeanResponse>(`${url}${coffeeBeanPaths.deactivate(id)}`, {}, authHeaders());
    return response.data.data;
}

// ── Capsules (Coffee Master setup) ───────────────────────────────────────────
export async function listMyCapsules(): Promise<CoffeeCapsule[]> {
    const response = await apiClient.get<CapsuleListResponse>(`${url}${coffeeCapsulePaths.list()}`, authHeaders());
    return response.data.data;
}
export async function registerCapsule(payload: RegisterCapsulePayload): Promise<CoffeeCapsule> {
    const response = await apiClient.post<CapsuleResponse>(`${url}${coffeeCapsulePaths.create()}`, payload, authHeaders());
    return response.data.data;
}
export async function updateCapsule(id: string, patch: UpdateCapsulePayload): Promise<CoffeeCapsule> {
    const response = await apiClient.patch<CapsuleResponse>(`${url}${coffeeCapsulePaths.byId(id)}`, patch, authHeaders());
    return response.data.data;
}
export async function deactivateCapsule(id: string): Promise<CoffeeCapsule> {
    const response = await apiClient.patch<CapsuleResponse>(`${url}${coffeeCapsulePaths.deactivate(id)}`, {}, authHeaders());
    return response.data.data;
}

// ── Coffee Master capability (status + request to be granted) ────────────────
// Probe whether the caller can use the Coffee Master setup, and the status of any
// pending/decided request. Drives the gate + request CTA in MachineManager.
export async function getCoffeeMasterStatus(): Promise<CoffeeMasterStatus> {
    const response = await apiClient.get<CoffeeMasterStatusResponse>(`${url}${coffeeMasterPaths.me()}`, authHeaders());
    return response.data.data;
}
// Ask to be granted the Coffee Master capability (notifies the admin). Idempotent server-side.
export async function requestCoffeeMaster(message?: string): Promise<{ status: string }> {
    const response = await apiClient.post<{ success: boolean; data: { status?: string; alreadyCoffeeMaster?: boolean } }>(
        `${url}${coffeeMasterPaths.request()}`,
        { message },
        authHeaders(),
    );
    return { status: response.data.data?.status ?? (response.data.data?.alreadyCoffeeMaster ? 'approved' : 'pending') };
}

// ── Coffee Master admin review queue (admin only) ────────────────────────────
export async function listCoffeeMasterRequests(
    status: 'pending' | 'all' = 'pending',
): Promise<{ requests: CoffeeMasterRequest[]; pending: number }> {
    const response = await apiClient.get<CoffeeMasterRequestsResponse>(
        `${url}${coffeeMasterPaths.requests()}?status=${status}`,
        authHeaders(),
    );
    return { requests: response.data.data, pending: response.data.stats?.pending ?? 0 };
}
export async function approveCoffeeMasterRequest(id: string): Promise<void> {
    await apiClient.post(`${url}${coffeeMasterPaths.approve(id)}`, {}, authHeaders());
}
export async function rejectCoffeeMasterRequest(id: string): Promise<void> {
    await apiClient.post(`${url}${coffeeMasterPaths.reject(id)}`, {}, authHeaders());
}
